import React, { useContext, useState } from "react";
import axios from "axios";
import { AuthContext } from "../../../contexts";
import { CircularProgress, Tab, Tabs } from "@mui/material";
import dayjs from "dayjs";
import { Bar, BarChart, CartesianGrid, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis, Cell } from "recharts";
import { toast } from "react-toastify";
import toastOptions from "../../../assets/constants/toast";
import { ArrowsClockwiseIcon, HeadsetIcon } from "@phosphor-icons/react";
import { CustomTag, CustomPageHeader } from "../../../ui";

const Support = () => {
    const [isLoaded, setIsLoaded] = useState(false);
    const [refreshData, setRefreshData] = useState(0);
    const [mergedData, setMergedData] = useState([]);
    const [lastUpdated, setLastUpdated] = useState({
        desk: null,
        zammad: null,
    });
    const { token } = useContext(AuthContext);
    const [tab, setTab] = useState(0);
    const [subTab, setSubTab] = useState(0);

    React.useEffect(() => {
        fetchCombinedData();
    }, [refreshData, token]);

    const fetchCombinedData = async () => {
        setIsLoaded(false);

        try {
            const customersResponse = await axios.get(process.env.REACT_APP_UPA_API_HOST + "pennylane/customers", {
                headers: { Authorization: "Bearer " + token },
            });

            const customers = customersResponse.data || [];

            const [deskResponse, zammadResponse] = await Promise.all([
                axios
                    .get(process.env.REACT_APP_UPA_API_HOST + "desk/get-hours", {
                        headers: { Authorization: "Bearer " + token },
                    })
                    .catch(() => ({ data: { hours: [], lastUpdated: null } })),
                axios
                    .get(process.env.REACT_APP_UPA_API_HOST + "zammad/support-hours", {
                        headers: { Authorization: "Bearer " + token },
                    })
                    .catch(() => ({ data: { hours: [], lastUpdated: null } })),
            ]);

            const deskData = deskResponse.data?.hours || [];
            const zammadData = zammadResponse.data?.hours || [];

            setLastUpdated({
                desk: deskResponse.data?.lastUpdated,
                zammad: zammadResponse.data?.lastUpdated,
            });

            const merged = mergeSupportData(deskData, zammadData, customers);
            setMergedData(merged);
        } catch (error) {
            console.error("Error fetching support data:", error);
        } finally {
            setIsLoaded(true);
        }
    };

    const detectSupportPlan = (clientName, customerData = null) => {
        if (customerData && customerData.supportInfo) {
            const supportInfo = customerData.supportInfo;

            if (supportInfo.isMutualized) {
                return {
                    type: "mutualized",
                    hours: supportInfo.premiumHours || 0,
                    source: "pennylane_groupedSupport",
                    groupInfo: supportInfo.groupInfo,
                };
            }

            if (supportInfo.isPremium) {
                return {
                    type: "premium",
                    hours: supportInfo.premiumHours || 6,
                    source: "pennylane_supportInfo",
                };
            }

            if (supportInfo.isEssential) {
                return {
                    type: "essential",
                    hours: 0,
                    source: "pennylane_supportInfo",
                };
            }

            return {
                type: "no-assistance",
                hours: 0,
                source: "pennylane_supportInfo",
            };
        }

        if (clientName) {
            const name = clientName.toLowerCase();
            const supportCodeMatch = name.match(/alt(\d+)sup/);
            if (supportCodeMatch) {
                const hours = parseInt(supportCodeMatch[1]);
                return {
                    type: "custom",
                    hours: hours,
                    source: "namePattern",
                };
            }
        }

        return {
            type: "no-assistance",
            hours: 0,
            source: "default",
        };
    };

    const mergeSupportData = (deskData, zammadData, customers) => {
        const yearMap = new Map();

        const customerIndex = new Map();
        const customerIndexById = new Map();
        customers.forEach((customer) => {
            customerIndex.set(customer.name.toLowerCase(), customer);
            customerIndexById.set(String(customer.id), customer);
        });

        // Zammad hours carry the linked Pennylane customer id, which stays reliable
        // when the Zammad name differs from the Pennylane one.
        const findCustomer = (client, clientName) =>
            (client.pennylaneCustomerId && customerIndexById.get(String(client.pennylaneCustomerId))) || customerIndex.get(clientName.toLowerCase());

        deskData.forEach((yearData) => {
            const year = yearData.year;
            if (!yearMap.has(year)) {
                yearMap.set(year, { year, clients: new Map() });
            }

            const yearEntry = yearMap.get(year);
            const clientData = yearData.customers || yearData.contacts || [];

            clientData.forEach((client) => {
                const clientName = client.customerName || client.contactName;
                const hours = client.hoursConsumed || 0;

                if (!yearEntry.clients.has(clientName)) {
                    const customerData = findCustomer(client, clientName);
                    const supportPlan = detectSupportPlan(clientName, customerData);

                    yearEntry.clients.set(clientName, {
                        clientName,
                        deskHours: 0,
                        zammadHours: 0,
                        totalHours: 0,
                        deskPercentage: 0,
                        zammadPercentage: 0,
                        supportPlan: supportPlan,
                        includedHours: supportPlan.hours,
                        exceededHours: 0,
                        isExceeded: false,
                        rawClient: client,
                        customerData: customerData,
                    });
                }

                const clientEntry = yearEntry.clients.get(clientName);
                clientEntry.deskHours += hours;
                clientEntry.totalHours += hours;
            });
        });

        zammadData.forEach((yearData) => {
            const year = yearData.year;
            if (!yearMap.has(year)) {
                yearMap.set(year, { year, clients: new Map() });
            }

            const yearEntry = yearMap.get(year);
            const clientData = yearData.customers || yearData.contacts || [];

            clientData.forEach((client) => {
                const clientName = client.customerName || client.contactName;
                const hours = client.hoursConsumed || 0;

                if (!yearEntry.clients.has(clientName)) {
                    const customerData = findCustomer(client, clientName);
                    const supportPlan = detectSupportPlan(clientName, customerData);

                    yearEntry.clients.set(clientName, {
                        clientName,
                        deskHours: 0,
                        zammadHours: 0,
                        totalHours: 0,
                        deskPercentage: 0,
                        zammadPercentage: 0,
                        supportPlan: supportPlan,
                        includedHours: supportPlan.hours,
                        exceededHours: 0,
                        isExceeded: false,
                        rawClient: client,
                        customerData: customerData,
                    });
                }

                const clientEntry = yearEntry.clients.get(clientName);
                clientEntry.zammadHours += hours;
                clientEntry.totalHours += hours;
            });
        });

        const result = Array.from(yearMap.values())
            .map((yearEntry) => ({
                year: yearEntry.year,
                clients: Array.from(yearEntry.clients.values())
                    .map((client) => {
                        if (client.totalHours > 0) {
                            client.deskPercentage = Math.round((client.deskHours / client.totalHours) * 100);
                            client.zammadPercentage = Math.round((client.zammadHours / client.totalHours) * 100);
                        }

                        client.exceededHours = Math.max(0, client.totalHours - client.includedHours);
                        client.isExceeded = client.totalHours > client.includedHours;

                        return client;
                    })
                    .sort((a, b) => b.totalHours - a.totalHours),
            }))
            .sort((a, b) => b.year - a.year);

        return result;
    };

    const handleRefreshData = async () => {
        try {
            await toast.promise(
                axios.post(
                    process.env.REACT_APP_UPA_API_HOST + "zammad/import",
                    {},
                    {
                        headers: { Authorization: "Bearer " + token },
                    }
                ),
                {
                    pending: "Importation des données Zammad en cours...",
                    success: (data) => {
                        const stats = data.data;
                        return `Importation réussie : ${stats.ticketsImported || 0} tickets traités, ${stats.timeEntriesImported || 0} entrées temporelles récupérées`;
                    },
                    error: "Échec de l'importation des données Zammad",
                },
                toastOptions
            );

            await toast.promise(
                axios.post(
                    process.env.REACT_APP_UPA_API_HOST + "zammad/refresh-combined-hours",
                    {},
                    {
                        headers: { Authorization: "Bearer " + token },
                    }
                ),
                {
                    pending: "Recalcul des heures de support combinées...",
                    success: (data) => {
                        const result = data.data;
                        return `Recalcul terminé : ${result.customersProcessed || 0} clients traités`;
                    },
                    error: "Échec du recalcul des heures combinées",
                },
                toastOptions
            );

            window.dispatchEvent(new CustomEvent("zammadDataUpdated"));

            setRefreshData((prev) => prev + 1);
        } catch (error) {
            console.error("Erreur lors de la mise à jour:", error);
        }
    };

    const CustomSupportTooltip = ({ active, payload, label, isPremiumTab, isMutualizedTab }) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload;
            return (
                <div className="support-tooltip">
                    <div className="support-tooltip__title">{label}</div>

                    <div className={`support-tooltip__hours ${data.isExceeded ? "support-tooltip__hours--exceeded" : ""}`}>
                        {isMutualizedTab
                            ? `${data.totalHours % 1 === 0 ? data.totalHours.toFixed(0) : data.totalHours.toFixed(1)}h / ${data.allocatedHours}h`
                            : isPremiumTab
                            ? `${data.totalHours % 1 === 0 ? data.totalHours.toFixed(0) : data.totalHours.toFixed(1)}h / ${data.includedHours}h`
                            : `${data.totalHours % 1 === 0 ? data.totalHours.toFixed(0) : data.totalHours.toFixed(1)}h / ${data.includedHours}h`}
                        {data.isExceeded && <span className="support-tooltip__warning">⚠️</span>}
                    </div>

                    <div className="support-tooltip__details">
                        {isMutualizedTab && data.children && (
                            <div>
                                <div>
                                    <strong>Enfants ({data.children.length}) :</strong>
                                </div>
                                <ul>
                                    {data.children.map((child, index) => (
                                        <li key={index}>
                                            {child.clientName} : <strong>{(child.totalHours || 0).toFixed(1)}h</strong>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {data.deskHours > 0 && (
                            <div>
                                Desk : <strong>{data.deskHours % 1 === 0 ? data.deskHours.toFixed(0) : data.deskHours.toFixed(1)}h</strong>
                            </div>
                        )}

                        {data.zammadHours > 0 && (
                            <div>
                                Zammad : <strong>{data.zammadHours % 1 === 0 ? data.zammadHours.toFixed(0) : data.zammadHours.toFixed(1)}h</strong>
                            </div>
                        )}
                    </div>
                </div>
            );
        }
        return null;
    };

    const CustomYAxisTick = (props) => {
        const { x, y, payload } = props;
        const client = payload.value;
        const clientData = props.data?.find((item) => item.clientName === client);

        const getPlanClass = (type) => {
            switch (type) {
                case "premium":
                    return "purple";
                case "essential":
                    return "blue";
                case "no-assistance":
                default:
                    return "deactivated";
            }
        };

        return (
            <g>
                <foreignObject x={x - 300} y={y - 10} width="300" height="31">
                    <div className="support-chart__yaxis-tick">
                        {clientData ? (
                            <CustomTag value={clientData.clientName} className={getPlanClass(clientData.supportPlan?.type)} />
                        ) : (
                            <span className="support-chart__yaxis-fallback">{client}</span>
                        )}
                    </div>
                </foreignObject>
            </g>
        );
    };

    const renderMutualizedChart = (clients) => {
        const groupsMap = new Map();

        clients.forEach((client) => {
            const groupInfo = client.customerData?.supportInfo?.groupInfo;
            const groupKey = groupInfo?.parentCustomerId || "unknown";

            if (!groupsMap.has(groupKey)) {
                groupsMap.set(groupKey, {
                    parentName: groupInfo?.parentCustomerName || "Groupe inconnu",
                    children: [],
                    totalHours: 0,
                    allocatedHours: groupInfo?.totalAvailableHours || client.customerData?.supportInfo?.premiumHours || client.supportPlan?.hours || 0,
                });
            }

            const group = groupsMap.get(groupKey);
            group.children.push(client);
            group.totalHours += client.totalHours || 0;
        });

        const chartData = Array.from(groupsMap.values()).map((group) => {
            const usagePercentage = group.allocatedHours > 0 ? (group.totalHours / group.allocatedHours) * 100 : 0;
            const exceededPercentage = Math.max(0, usagePercentage - 100);
            const realConsumedPercentage = Math.min(usagePercentage, 100);
            const consumedPercentage = Math.max(realConsumedPercentage, 10);
            const remainingPercentage = Math.max(0, 100 - consumedPercentage);
            const exceededHours = Math.max(0, group.totalHours - group.allocatedHours);

            return {
                groupName: group.parentName,
                totalHours: group.totalHours,
                allocatedHours: group.allocatedHours,
                childrenCount: group.children.length,
                usagePercentage,
                consumedPercentage,
                remainingPercentage,
                exceededPercentage,
                exceededHours,
                displayHours: `${group.totalHours % 1 === 0 ? group.totalHours.toFixed(0) : group.totalHours.toFixed(1)}h/${group.allocatedHours}h`,
                exceededHoursLabel: exceededHours > 0 ? `+${exceededHours % 1 === 0 ? exceededHours.toFixed(0) : exceededHours.toFixed(1)}h` : "",
                children: group.children,
            };
        });

        const maxPercentage = Math.max(...chartData.map((c) => c.usagePercentage), 100);

        return (
            <div className="support-hours-container">
                <ResponsiveContainer height={chartData.length * 40 + 100}>
                    <BarChart layout="vertical" data={chartData} margin={{ left: 20, right: 20, top: 20, bottom: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--black-10)" />
                        <YAxis dataKey="groupName" type="category" tick={{ fontSize: 14, fill: "var(--black-70)" }} minTickGap={0} width={360} />
                        <XAxis type="number" tick={{ fontSize: 12, fill: "var(--black-70)" }} domain={[0, maxPercentage]} tickFormatter={(value) => `${Math.round(value)}%`} />
                        <Tooltip content={<CustomSupportTooltip isMutualizedTab={true} />} />
                        <Bar
                            dataKey="consumedPercentage"
                            fill="var(--purple-500)"
                            radius={[0, 0, 0, 0]}
                            stackId="stack"
                            label={{
                                dataKey: "displayHours",
                                position: "insideLeft",
                                fontSize: 12,
                                fill: "var(--white-100)",
                                fontWeight: "bold",
                                offset: 8,
                            }}
                        />
                        <Bar dataKey="remainingPercentage" fill="var(--black-10)" radius={[0, 0, 0, 0]} stackId="stack" />
                        <Bar
                            dataKey="exceededPercentage"
                            fill="var(--red-500)"
                            radius={[0, 0, 0, 0]}
                            stackId="stack"
                            label={{
                                dataKey: "exceededHoursLabel",
                                position: "insideLeft",
                                fontSize: 12,
                                fill: "var(--white-100)",
                                fontWeight: "bold",
                                offset: 8,
                            }}
                        />
                        <ReferenceLine x={100} stroke="var(--black-30)" strokeDasharray="5 5" />
                    </BarChart>
                </ResponsiveContainer>
            </div>
        );
    };

    const renderPremiumChart = (clients) => {
        const chartData = clients.map((client) => {
            const usagePercentage = client.includedHours > 0 ? (client.totalHours / client.includedHours) * 100 : client.totalHours > 0 ? 100 : 0;
            const exceededPercentage = Math.max(0, usagePercentage - 100);
            const realConsumedPercentage = Math.min(usagePercentage, 100);
            const consumedPercentage = Math.max(realConsumedPercentage, 10);
            const remainingPercentage = Math.max(0, 100 - consumedPercentage);

            return {
                ...client,
                usagePercentage,
                consumedPercentage,
                remainingPercentage,
                exceededPercentage,
                displayHours: `${client.totalHours % 1 === 0 ? client.totalHours.toFixed(0) : client.totalHours.toFixed(1)}h/${client.includedHours}h`,
                exceededHoursLabel: client.exceededHours > 0 ? `+${client.exceededHours % 1 === 0 ? client.exceededHours.toFixed(0) : client.exceededHours.toFixed(1)}h` : "",
            };
        });

        const maxPercentage = Math.max(...chartData.map((c) => c.usagePercentage), 100);

        return (
            <div className="support-hours-container">
                <ResponsiveContainer height={clients.length * 40 + 100}>
                    <BarChart layout="vertical" data={chartData} margin={{ left: 20, right: 20, top: 20, bottom: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--black-10)" />
                        <YAxis dataKey="clientName" type="category" tick={{ fontSize: 14, fill: "var(--black-70)" }} minTickGap={0} width={360} />
                        <XAxis type="number" tick={{ fontSize: 12, fill: "var(--black-70)" }} domain={[0, maxPercentage]} tickFormatter={(value) => `${Math.round(value)}%`} />
                        <Tooltip content={<CustomSupportTooltip isPremiumTab={true} />} />
                        <Bar
                            dataKey="consumedPercentage"
                            fill="var(--blue-500)"
                            radius={[0, 0, 0, 0]}
                            stackId="stack"
                            label={{
                                dataKey: "displayHours",
                                position: "insideLeft",
                                fontSize: 12,
                                fill: "var(--white-100)",
                                fontWeight: "bold",
                                offset: 8,
                            }}
                        />
                        <Bar dataKey="remainingPercentage" fill="var(--black-10)" radius={[0, 0, 0, 0]} stackId="stack" />
                        <Bar
                            dataKey="exceededPercentage"
                            fill="var(--red-500)"
                            radius={[0, 0, 0, 0]}
                            stackId="stack"
                            label={{
                                dataKey: "exceededHoursLabel",
                                position: "insideLeft",
                                fontSize: 12,
                                fill: "var(--white-100)",
                                fontWeight: "bold",
                                offset: 8,
                            }}
                        />
                        <ReferenceLine x={100} strokeWidth={2} stroke="var(--red-500)" strokeDasharray="3 3" label={{ value: "100%", position: "top", fill: "var(--red-500)" }} />
                    </BarChart>
                </ResponsiveContainer>
            </div>
        );
    };

    const renderEssentialChart = (clients) => {
        const chartData = clients.map((client) => {
            const displayExceeded = Math.max(client.exceededHours, 0.5);
            return {
                ...client,
                exceededHours: displayExceeded,
                displayHours: `+${client.exceededHours % 1 === 0 ? client.exceededHours.toFixed(0) : client.exceededHours.toFixed(1)}h`,
            };
        });

        const maxExceededHours = Math.max(...chartData.map((c) => c.exceededHours), 10);

        return (
            <div className="support-hours-container">
                <div className="support-chart__legend">
                    <span className="support-chart__legend-title">Types de support :</span>
                    <div className="support-chart__legend-item">
                        <CustomTag value="Essentiel" className="blue uppercase" />
                    </div>
                    <div className="support-chart__legend-item">
                        <CustomTag value="Aucun" className="deactivated uppercase" />
                    </div>
                </div>

                <ResponsiveContainer height={clients.length * 40 + 100}>
                    <BarChart layout="vertical" data={chartData} margin={{ left: 20, right: 20, top: 20, bottom: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--black-10)" />
                        <YAxis dataKey="clientName" type="category" tickSize={20} tick={<CustomYAxisTick data={chartData} />} minTickGap={0} width={360} />
                        <XAxis type="number" tick={{ fontSize: 12, fill: "var(--black-70)" }} domain={[0, maxExceededHours]} tickFormatter={(value) => `${value}h`} />
                        <Tooltip content={<CustomSupportTooltip isPremiumTab={false} />} />
                        <Bar
                            dataKey="exceededHours"
                            fill="var(--red-500)"
                            radius={[0, 0, 0, 0]}
                            label={{
                                dataKey: "displayHours",
                                position: "insideLeft",
                                fontSize: 12,
                                fill: "var(--white-100)",
                                fontWeight: "bold",
                                offset: 8,
                            }}
                        >
                            {chartData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.exceededHours > 0 ? "var(--red-500)" : "var(--blue-500)"} />
                            ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>
        );
    };

    return isLoaded ? (
        <div className="c-app__view">
            <CustomPageHeader 
                title="Suivi du support"
                titleIcon={{
                    icon: HeadsetIcon,
                }}
                lastUpdatedAt={lastUpdated?.zammad}
                buttons={[
                    {
                        label: "Mettre à jour",
                        onClick: handleRefreshData,
                        icon: ArrowsClockwiseIcon,
                        variant: "primary"
                    }
                ]}
            />

            {mergedData && mergedData.length > 0 ? (
                <div className="tabs-container">
                    <Tabs
                        value={tab}
                        onChange={(event, newValue) => {
                            setTab(newValue);
                            setSubTab(0);
                        }}
                    >
                        {mergedData.map((year, index) => (
                            <Tab key={index} value={index} label={`${year.year} (${year.clients.length})`} />
                        ))}
                    </Tabs>
                    {mergedData.map((year, index) => {
                        const premiumClients = year.clients.filter((client) => client.supportPlan?.type === "premium");
                        const mutualizedClients = year.clients.filter((client) => client.supportPlan?.type === "mutualized");
                        const nonPremiumClients = year.clients.filter((client) => client.supportPlan?.type !== "premium" && client.supportPlan?.type !== "mutualized");

                        return (
                            <div key={index} role="tabpanel" id={index} hidden={tab !== index}>
                                <div className="sub-tabs-container">
                                    <Tabs value={subTab} onChange={(event, newValue) => setSubTab(newValue)}>
                                        <Tab value={0} label={`Premium (${premiumClients.length})`} />
                                        <Tab value={1} label={`Mutualisé (${mutualizedClients.length})`} />
                                        <Tab value={2} label={`Essentiel/Aucun (${nonPremiumClients.length})`} />
                                    </Tabs>

                                    <div role="tabpanel" hidden={subTab !== 0}>
                                        {renderPremiumChart(premiumClients)}
                                    </div>

                                    <div role="tabpanel" hidden={subTab !== 1}>
                                        {renderMutualizedChart(mutualizedClients)}
                                    </div>

                                    <div role="tabpanel" hidden={subTab !== 2}>
                                        {renderEssentialChart(nonPremiumClients)}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div className="no-data-message">
                    <p>Aucune donnée de support disponible. Essayez de mettre à jour les données.</p>
                </div>
            )}
        </div>
    ) : (
        <div className="loader-container absolute">
            <CircularProgress size={30} />
        </div>
    );
};

export default Support;
